/**
 * Locale-aware display formats for moment objects.
 *
 * Hand-written patterns such as 'MMM D, YYYY' or 'h:mm A' put the parts in
 * English order ("sept. 28, 2026" in Spanish, "9月 28, 2026" in Chinese). Use
 * these presets for anything shown to users instead. They follow the moment
 * locale of the value being formatted, which the apps keep in sync with the
 * UI language. Keep plain patterns for API payloads, parsing, and exports.
 */

// Presets moment already localizes through its long-date tokens.
const MOMENT_TOKENS = {
  date: 'll', // Sep 28, 2026 | 28 sept. 2026 | 2026年9月28日
  dateLong: 'LL', // September 28, 2026
  numericDate: 'L', // 09/28/2026 | 2026-09-28 | 2026/09/28
  time: 'LT', // 8:30 PM | 20:30
  timeWithSeconds: 'LTS',
  dateTime: 'lll', // Sep 28, 2026 8:30 PM
  dateTimeLong: 'LLL', // September 28, 2026 8:30 PM
};

// Presets moment has no token for, per base language. English is the fallback.
const LOCALIZED_PATTERNS = {
  monthDay: {
    en: 'MMM D',
    es: 'D [de] MMM',
    fr: 'D MMM',
    zh: 'MMMDo',
    de: 'D. MMM',
    ja: 'M月D日',
    pt: 'D [de] MMM',
  },
  monthDayLong: {
    en: 'MMMM D',
    es: 'D [de] MMMM',
    fr: 'D MMMM',
    zh: 'MMMDo',
    de: 'D. MMMM',
    ja: 'M月D日',
    pt: 'D [de] MMMM',
  },
  weekdayMonthDay: {
    en: 'ddd, MMM D',
    es: 'ddd, D [de] MMM',
    fr: 'ddd D MMM',
    zh: 'MMMDo ddd',
    de: 'ddd, D. MMM',
    ja: 'M月D日(ddd)',
    pt: 'ddd, D [de] MMM',
  },
  weekdayMonthDayLong: {
    en: 'dddd, MMMM D',
    es: 'dddd, D [de] MMMM',
    fr: 'dddd D MMMM',
    zh: 'MMMDo dddd',
    de: 'dddd, D. MMMM',
    ja: 'M月D日 dddd',
    pt: 'dddd, D [de] MMMM',
  },
  weekdayDate: {
    en: 'ddd, MMM D, YYYY',
    es: 'ddd, D [de] MMM [de] YYYY',
    fr: 'ddd D MMM YYYY',
    zh: 'YYYY年MMMDo ddd',
    de: 'ddd, D. MMM YYYY',
    ja: 'YYYY年M月D日(ddd)',
    pt: 'ddd, D [de] MMM [de] YYYY',
  },
  weekdayDateLong: {
    en: 'dddd, MMMM D, YYYY',
    es: 'dddd, D [de] MMMM [de] YYYY',
    fr: 'dddd D MMMM YYYY',
    zh: 'YYYY年MMMDo dddd',
    de: 'dddd, D. MMMM YYYY',
    ja: 'YYYY年M月D日 dddd',
    pt: 'dddd, D [de] MMMM [de] YYYY',
  },
  monthYear: {
    en: 'MMMM YYYY',
    es: 'MMMM [de] YYYY',
    fr: 'MMMM YYYY',
    zh: 'YYYY年M月',
    de: 'MMMM YYYY',
    ja: 'YYYY年M月',
    pt: 'MMMM [de] YYYY',
  },
};

const DISPLAY_DATE_PRESETS = [
  ...Object.keys(MOMENT_TOKENS),
  ...Object.keys(LOCALIZED_PATTERNS),
];

const displayDatePattern = (preset, locale) => {
  if (MOMENT_TOKENS[preset]) return MOMENT_TOKENS[preset];
  const patterns = LOCALIZED_PATTERNS[preset];
  if (!patterns) throw new Error(`Unknown display date preset: ${preset}`);
  const baseLang = String(locale || 'en')
    .toLowerCase()
    .split('-')[0];
  return patterns[baseLang] || patterns.en;
};

/**
 * Format a moment for display.
 *
 * @param {import('moment').Moment} value A moment (moment-timezone objects keep their zone).
 * @param {string} preset One of DISPLAY_DATE_PRESETS.
 * @param {{ time?: boolean, zone?: boolean }} [options] Append the localized
 *   time (LT) and/or the time zone abbreviation (z, needs moment-timezone).
 * @returns {string}
 */
const formatDisplayDate = (
  value,
  preset,
  { time = false, zone = false } = {}
) => {
  if (!value || typeof value.format !== 'function' || !value.isValid()) {
    return '';
  }
  const parts = [value.format(displayDatePattern(preset, value.locale()))];
  if (time) parts.push(value.format('LT'));
  if (zone) parts.push(value.format('z'));
  return parts.filter(Boolean).join(' ');
};

module.exports = {
  DISPLAY_DATE_PRESETS,
  displayDatePattern,
  formatDisplayDate,
};
